/**
 * Viewer-capability helpers for the account lifecycle band.
 *
 * Each control maps to the same permission the matching surface already
 * enforces, so the band never shows a button the RFC tab / CBS checklist /
 * skip mutation would hide or reject:
 *
 *   Skip / SWOT admin          → accounts.admin (or internal tenant.access_all)
 *   Move stage                 → accounts.move_pipeline_stage_manual (API editableSubStages)
 *   Edit stage dates           → tenant.access_all
 *   Approve RFC (clientOnly)   → workflow.approve_rfc (RfcTab / rfc.adjudicate)
 *   CBS next actions           → rfc.cbs_queue (submitToCbs / advanceCbsStage)
 */

import {CODE_PERMISSIONS} from '@common/permissions';

export type LifecycleViewerCaps = {
  /** Internal user holding tenant.access_all — skip, date edits, RFC-approval override. */
  isInternalAdmin: boolean;
  isBroker: boolean;
  /** Client profile holding workflow.approve_rfc — client-only next actions. */
  isClientAdmin: boolean;
  /** Internal user holding rfc.cbs_queue — CBS checklist next actions. */
  canDriveCbs: boolean;
  canSkip: boolean;
  canEditStageDates: boolean;
  swotIsAdmin: boolean;
};

export function resolveLifecycleViewerCaps(args: {
  isInternalUser: boolean;
  isBroker: boolean;
  hasClientProfile: boolean;
  permissions: ReadonlySet<string>;
}): LifecycleViewerCaps {
  const isInternalAdmin =
    args.isInternalUser && args.permissions.has(CODE_PERMISSIONS.tenant.accessAll.key);
  const canDriveCbs =
    args.isInternalUser && args.permissions.has(CODE_PERMISSIONS.rfc.cbsQueue.key);
  const isAccountsAdmin =
    args.hasClientProfile && args.permissions.has(CODE_PERMISSIONS.accounts.admin.key);
  const isClientAdmin =
    args.hasClientProfile && args.permissions.has(CODE_PERMISSIONS.workflow.approveRfc.key);

  return {
    isInternalAdmin,
    isBroker: args.isBroker,
    isClientAdmin,
    canDriveCbs,
    canSkip: isAccountsAdmin || isInternalAdmin,
    canEditStageDates: isInternalAdmin,
    swotIsAdmin: isAccountsAdmin || isInternalAdmin,
  };
}

export type NextActionCaps = Pick<
  LifecycleViewerCaps,
  'isInternalAdmin' | 'isBroker' | 'isClientAdmin' | 'canDriveCbs'
>;

/** True when the descriptor's role gate hides the button from this viewer. */
export function isNextActionBlocked(
  action: {adminOnly?: boolean; brokerOnly?: boolean; clientOnly?: boolean},
  caps: NextActionCaps,
): boolean {
  return (
    (!!action.adminOnly && !caps.canDriveCbs) ||
    (!!action.brokerOnly && !caps.isBroker && !caps.isClientAdmin && !caps.isInternalAdmin) ||
    (!!action.clientOnly && !caps.isClientAdmin && !caps.isInternalAdmin)
  );
}
